import React, { useRef } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';

const TestimonialBlock = ({ data, isSelected, onUpdate, previewMode, blockId, onSelectBlock }) => {
  const fileInputRef = useRef(null);
  
  const {
    quote = 'This is an amazing product that has helped our business grow!',
    author = 'John Doe',
    position = 'CEO',
    companyName = 'Company Name',
    authorImage = '',
    backgroundColor = '#f8f9fa',
    textColor = '#333333',
    quoteColor = '#333333',
    authorColor = '#666666',
    positionColor = '#999999',
    companyColor = '#999999',
    quoteFontSize = 16,
    authorFontSize = 14,
    borderRadius = 8,
    alignment = 'center',
    fontFamily = 'Arial, sans-serif',
    marginTop = 20,
    marginBottom = 20,
    padding = 30,
  } = data;

  const getResponsiveFontSize = () => {
    switch (previewMode) {
      case 'mobile':
        return Math.max(quoteFontSize * 0.9, 14);
      case 'tablet':
        return Math.max(quoteFontSize * 0.95, 15);
      default:
        return quoteFontSize;
    }
  };

  const getResponsivePadding = () => {
    switch (previewMode) {
      case 'mobile':
        return '20px 15px';
      case 'tablet':
        return '25px 20px';
      default:
        return `${padding}px`;
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const updatedData = { authorImage: e.target.result };
        if (onUpdate) {
          onUpdate(updatedData);
        }
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  return (
    <Box
      sx={{
        padding: getResponsivePadding(),
        marginTop: `${marginTop}px`,
        marginBottom: `${marginBottom}px`,
        backgroundColor,
        border: isSelected ? '1px dashed #007bff' : 'none',
        borderRadius: isSelected ? '4px' : `${borderRadius}px`,
        textAlign: alignment,
        fontFamily,
      }}
    >
      {/* Avatar Section */}
      <Box sx={{
        display: 'flex',
        justifyContent: alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center',
        mb: 2,
      }}>
        {authorImage ? (
        <Avatar
            src={authorImage}
          alt={author}
          sx={{
            width: 60,
            height: 60,
            backgroundColor: '#e0e0e0',
          }}
        />
        ) : (
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: '#e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#d0d0d0',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload sx={{ color: '#666', fontSize: 24 }} />
          </Box>
      )}
      </Box>
      
      <Typography
        variant="body1"
        sx={{
          fontSize: `${getResponsiveFontSize()}px`,
          color: quoteColor || textColor,
          fontStyle: 'italic',
          lineHeight: 1.6,
          mb: 2,
          fontFamily,
          textAlign: alignment,
        }}
      >
        "{quote}"
      </Typography>
      
      <Typography
        variant="body2"
        sx={{
          fontSize: `${authorFontSize}px`,
          color: authorColor,
          fontWeight: 'bold',
          fontFamily,
          textAlign: alignment,
        }}
      >
        — {author}
      </Typography>
      
      {position && (
        <Typography
          variant="body2"
          sx={{
            fontSize: `${authorFontSize * 0.9}px`,
            color: positionColor,
            mt: 0.5,
            fontFamily,
            textAlign: alignment,
          }}
        >
          {position}
        </Typography>
      )}
      
      {companyName && (
        <Typography
          variant="body2"
          sx={{
            fontSize: `${authorFontSize * 0.9}px`,
            color: companyColor,
            mt: 0.5,
            fontFamily,
            textAlign: alignment,
          }}
        >
          {companyName}
        </Typography>
      )}
      
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />
    </Box>
  );
};

export default TestimonialBlock; 